// Contract between the native react-native-webview host (EXP-1507) and this page.
//
// Handshake: native loads this page over HTTPS (`source={{ uri }}`). On mount the
// page defines `window.AirwallexScaBridge.init` and posts `bridge_ready`. Native
// must wait for `bridge_ready` before injecting params, because the global is
// defined after React mounts — a one-shot `injectedJavaScript` at load would race
// it. Native then calls `window.AirwallexScaBridge.init(params)` via the WebView
// ref's `injectJavaScript(...)`, and calls it again with fresh params after
// `setup_complete` (the re-authorize loop).
//
// Inbound (native -> page): SCA params via `window.AirwallexScaBridge.init(params)`
// (see global.d.ts). Params are never read from the URL, so no auth material lands
// in a loggable URL.
//
// Outbound (page -> native): lifecycle events are posted as JSON strings to
// `window.ReactNativeWebView` (native parses `JSON.parse(event.nativeEvent.data)`
// and switches on `type`). Tokens are forwarded but never logged.

export type ScaEnvironment = 'demo' | 'prod';

export interface ScaInitParams {
  env: ScaEnvironment;
  clientId: string;
  authCode: string;
  codeVerifier: string;
  scaSessionCode: string;
  userEmail: string;
  // Pre-built Airwallex theme object produced by native; passed straight to
  // `createElement`. Its internal shape is owned by the SDK, so it stays opaque
  // here rather than re-deriving it on the page.
  theme?: Record<string, unknown>;
}

/**
 * Events forwarded to native. `verificationSucceed` carries the one-time SCA
 * token for the reveal retry; `setup_complete` signals first-time enrolment
 * finished with no token, so native must re-authorize and re-init.
 */
export type ScaBridgeEvent =
  | { type: 'bridge_ready' }
  | { type: 'ready' }
  | { type: 'verificationSucceed'; token: string }
  | { type: 'verificationFailed'; reason?: string }
  | { type: 'cancel' }
  | { type: 'error'; message?: string }
  | { type: 'missing_token' }
  | { type: 'init_failed' }
  | { type: 'setup_complete' };

export const postToNative = (event: ScaBridgeEvent): void => {
  window.ReactNativeWebView?.postMessage(JSON.stringify(event));
};

export interface ScaVerifyEventData {
  token?: string;
  reason?: string;
  message?: string;
}

// Minimal surface of the Airwallex `scaVerify` element that we rely on.
export interface ScaVerifyElement {
  on: (event: string, handler: (data?: ScaVerifyEventData) => void) => void;
  mount: (target: HTMLElement) => void;
  destroy: () => void;
}
